import { PosEvent } from '@core/bus/events'
import type { Plugin, PluginContext } from '@core/bus/types'
import { META_MERCURY_LOGIN, META_MERCURY_PASSWORD } from './meta'
import { debugLog } from '@core/utils/log'

/** Статус ветеринарного сопроводительного документа (ВСД). */
export type VsdStatus = 'incoming' | 'repaid'

/** Входящий ВСД (накладная на партию товара) в ФГИС Меркурий. */
export interface Vsd {
  /** Уникальный номер ВСД в системе Меркурий. */
  uuid: string
  /** Наименование продукции. */
  product: string
  /** Объём партии. */
  volume: string
  /** Грузоотправитель. */
  sender: string
  /** Дата оформления (ISO-строка). */
  issuedAt: string
  status: VsdStatus
}

/** Учётные данные API ВетИС (без ЭЦП/КриптоПро — только логин+пароль). */
export interface MercuryCredentials {
  login: string
  password: string
}

/** Результат пакетного гашения. */
export interface RepayResult {
  repaid: number
  total: number
}

/** Публичный API плагина Меркурий для админки директора. */
export interface RuMercuryPlugin extends Plugin {
  setCredentials(creds: MercuryCredentials): void
  getCredentials(): MercuryCredentials
  /** Получить список входящих ВСД (имитация выборки из ВетИС.API). */
  listVsd(): Promise<Vsd[]>
  /** Пакетно погасить все входящие ВСД (имитация SOAP-запроса). */
  repayAll(): Promise<RepayResult>
  setEnabled(enabled: boolean): void
  isEnabled(): boolean
}

/** Мок входящих накладных на молочку (имитация ответа ВетИС.API). */
function seedVsd(): Vsd[] {
  return [
    {
      uuid: 'VSD-0001',
      product: 'Молоко питьевое 3.2%, 930 мл',
      volume: '240 шт',
      sender: 'МК «Приволжский»',
      issuedAt: '2026-10-12',
      status: 'incoming'
    },
    {
      uuid: 'VSD-0002',
      product: 'Творог 5%, 200 г',
      volume: '120 шт',
      sender: 'ООО «Молочный Дом»',
      issuedAt: '2026-10-13',
      status: 'incoming'
    },
    {
      uuid: 'VSD-0003',
      product: 'Сметана 20%, 300 г',
      volume: '90 шт',
      sender: 'АО «Залесский фермер»',
      issuedAt: '2026-10-13',
      status: 'incoming'
    },
    {
      uuid: 'VSD-0004',
      product: 'Кефир 1%, 1 л',
      volume: '150 шт',
      sender: 'МК «Приволжский»',
      issuedAt: '2026-10-14',
      status: 'incoming'
    }
  ]
}

/**
 * Плагин «Меркурий» (ФГИС ВетИС).
 *
 * - Подписывается на SALE_COMPLETED (пост-обработка продажи).
 * - Хранит учётные данные API ВетИС (логин+пароль, без ЭЦП/КриптоПро) и
 *   предоставляет админке список входящих ВСД и пакетное гашение.
 * - repayAll() имитирует пакетный SOAP-запрос к ВетИС.API и переводит
 *   документы в статус «Погашено».
 */
export function createRuMercuryPlugin(): RuMercuryPlugin {
  let credentials: MercuryCredentials = { login: '', password: '' }
  let vsdList: Vsd[] = seedVsd()
  let enabled = true

  return {
    id: 'ru-mercury',
    country: 'RU',

    async init(ctx: PluginContext): Promise<void> {
      debugLog('[ru-mercury] init')
      if (ctx.db?.meta) {
        const [login, password] = await Promise.all([
          ctx.db.meta.get(META_MERCURY_LOGIN),
          ctx.db.meta.get(META_MERCURY_PASSWORD)
        ])
        credentials = {
          login: typeof login?.value === 'string' ? login.value : '',
          password: typeof password?.value === 'string' ? password.value : ''
        }
      }
      ctx.bus.on(PosEvent.SALE_COMPLETED, (sale) => {
        if (!enabled) return
        // Возврат: ВСД уже погашен при продаже, повторно не гасим (ARCH-TASK-65.5).
        if (sale.settlementType === 'income_return') {
          debugLog(`[ru-mercury] возврат по чеку ${sale.receiptId} — ВСД не трогаем`)
          return
        }
        debugLog(
          `[ru-mercury] SALE_COMPLETED: чек ${sale.receiptId}, сумма ${sale.total}`
        )
        // TODO(S5): автогашение ВСД по проданным молочным позициям.
      })
    },

    setCredentials(creds: MercuryCredentials): void {
      credentials = { ...creds }
    },

    getCredentials(): MercuryCredentials {
      return { ...credentials }
    },

    async listVsd(): Promise<Vsd[]> {
      if (!enabled) return []
      // Имитация сетевой выборки.
      await new Promise((r) => setTimeout(r, 120))
      return vsdList.map((v) => ({ ...v }))
    },

    async repayAll(): Promise<RepayResult> {
      if (!enabled) throw new Error('Модуль ФГИС Меркурий отключён')
      if (!credentials.login || !credentials.password) {
        throw new Error('Укажите логин и пароль API ВетИС в настройках')
      }
      const incoming = vsdList.filter((v) => v.status === 'incoming')
      if (incoming.length === 0) {
        return { repaid: 0, total: vsdList.length }
      }
      // Имитация пакетного SOAP-запроса к ВетИС.API (без ЭЦП/КриптоПро).
      debugLog(
        `[ru-mercury] пакетное гашение ${incoming.length} ВСД как ${credentials.login}…`
      )
      await new Promise((r) => setTimeout(r, 400))
      vsdList = vsdList.map((v) =>
        v.status === 'incoming' ? { ...v, status: 'repaid' } : v
      )
      return { repaid: incoming.length, total: vsdList.length }
    },

    setEnabled(value: boolean): void {
      enabled = value
      debugLog(`[ru-mercury] модуль ${value ? 'включён' : 'отключён'}`)
    },

    isEnabled(): boolean {
      return enabled
    }
  }
}

/** Готовый экземпляр плагина для регистрации в bootstrap. */
export const ruMercuryPlugin: RuMercuryPlugin = createRuMercuryPlugin()
